import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormsModule } from '@angular/forms';
import { auditTime, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ConfigService } from '../../shared-services/config.service';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { TooltiptableComponent } from '../tooltiptable/tooltiptable.component';
import { GlobalService } from '../../shared-services/global.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-tree-drill-down',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    MtxTooltipModule,
    TooltiptableComponent,
    DataErrorComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './tree-drill-down.component.html',
  styleUrl: './tree-drill-down.component.scss',
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', opacity: 0 })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
  ],
})
export class TreeDrillDownComponent {
  scoreTree: any;
  title: string = '';
  def: any;
  scores: any;
  @Input() data: any;
  filters: any;
  public hasError = false;
  public errorMessage = '';
  productId: any;
  portfolioOptions: any = [
    'UNKNOWN',
    'GROWTH',
    'CORE/NICHE',
    'CAUTION ZONE',
    'HARVEST',
  ];
  productPortfolio: string = 'UNKNOWN';
  loading = false;

  constructor(
    private service: ConfigService,
    private cdr: ChangeDetectorRef,
    private filterState: GlobalService,
    private snackBar: MatSnackBar
  ) { }

  private destroy$ = new Subject<void>();
  private previousExpansionState: any[] = [];
  @Output() simulate = new EventEmitter<void>();
  private bubbleClickSub?: Subscription;
  showCard = true;

  ngOnInit() {
    if (this.data?.close) {
      this.showCard = false;
    }
  }

  ngAfterViewInit() {
    // Basic data subscriptions
    this.initScores();
    this.initTitle();
    this.initDefinitions();

    if (this.data?.close) {
      this.initBubbleClick();
    }

    if (this.data?.updateOnclick) {
      this.initDrilldownDataSubscription();
    } else {
      this.initFilterSubscription();
    }
  }


  private initScores() {
    this.service.getScores()
      .pipe(takeUntil(this.destroy$))
      .subscribe(scores => this.scores = scores);
  }

  private initTitle() {
    this.service.drilldownTiltle$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data) {
          this.loading = true;
          this.title = data;
          this.cdr.detectChanges();
        }
      });
  }

  private initDefinitions() {
    this.service.getDef()
      .pipe(takeUntil(this.destroy$))
      .subscribe(def => this.def = def);
  }

  private initBubbleClick() {
    this.bubbleClickSub = this.service.bubbleClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe(widgetId => {
        this.showCard = widgetId === this.data.showOnSelectionOf;
        this.cdr.detectChanges();
      });
  }


  private initDrilldownDataSubscription() {
    this.service.drilldownData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((newData: any) => {
        if (
          newData?.widgetId &&
          this.data?.showOnSelectionOf &&
          newData.widgetId !== this.data.showOnSelectionOf
        ) {
          return;
        }
        if (newData?.data) {
          if (this.previousExpansionState.length) {
            this.reapplyExpandedState(newData.data, this.previousExpansionState);
          }
          this.scoreTree = [...newData.data];
          this.previousExpansionState = JSON.parse(JSON.stringify(newData.data));
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private initFilterSubscription() {
    this.filterState.filters$
      .pipe(
        auditTime(1000),
        filter(filters => !!filters),
        takeUntil(this.destroy$)
      )
      .subscribe(filters => {
        this.filters = { ...filters.filter };
        this.loadAndRenderChart(filters.filter);
      });
  }


  generateDrillDown(scenarioPayload: any) {
    this.service
      .customApi(this.data.apiUrl, scenarioPayload, this.data)
      .subscribe({
        next: (res) => {
          this.scoreTree = res.responseData[0].drillDownTree;
          this.productId = res.responseData[0]?.productId;
          this.loading = false;
          this.hasError = false;
        },
        error: (err) => {
          this.hasError = true;
          this.loading = false;
          this.errorMessage = 'No data found for the selected filters.';
        },
      });
  }
  private loadAndRenderChart(filters?: any): void {
    this.loading = true;

    const payload: any = {};

    if (this.data.customKeys && Array.isArray(this.data.customKeys)) {
      this.data.customKeys.forEach((item: any) => {
        const key = item.key;
        const filterKey = item.value;
        const filterValue = filters?.[filterKey]?.[0];
        if (filterValue !== undefined) {
          payload[key] = filterValue;
        }
      });
      this.service.customApi(this.data.apiUrl, payload, this.data).subscribe({
        next: (res) => {
          this.hasError = false;
          const match = res.responseData?.find(
            (item: any) => item?.periodType === this.data.periodType
          );
          this.productId = match?.productId;
          if (match?.drillDownTree) {
            this.scoreTree = Array.isArray(match.drillDownTree)
              ? match.drillDownTree
              : [match.drillDownTree];
          } else {
            this.scoreTree = [];
            this.hasError = true;
            this.errorMessage = 'No data found for the selected filters.';
          }
          this.loading = false;
        },
        error: (err) => {
          this.hasError = true;
          this.loading = false;
          this.errorMessage = 'No data found for the selected filters.';
        },
      });
    } else {
      if (this.data.setExtrakey) {
        const baseExtraKeys = ((this.data.extraKeys ?? []) as any[]).filter(
          (item: any) => !Object.prototype.hasOwnProperty.call(item || {}, 'version')
        );
        const selectedVersion = filters?.version?.[0];
        this.data.extraKeys =
          selectedVersion !== undefined && selectedVersion !== null && selectedVersion !== ''
            ? [...baseExtraKeys, { version: selectedVersion }]
            : baseExtraKeys;
      }
      this.service
        .fetchData(this.data.apiUrl, this.data.functionName, filters, this.data)
        .subscribe({
          next: (res) => {
            if (!this.data.periodType) {
              this.scoreTree = this.data?.responseKey
                ? res[0]
                  ? res[0][this.data.responseKey]
                  : res?.responseList?.[0]?.[this.data.mainKey][0][this.data.responseKey]
                : res;

              this.productPortfolio =
                res?.responseList?.[0]?.[this.data.mainKey][0].product_portfolio || this.portfolioOptions[0];
            } else {
              this.hasError = false;
              if (this.data.periodType == 'combined') {
                const responseData =
                  res?.responseList?.[0]?.responseData ??
                  res?.responseData ??
                  [];
                const mergedTree = this.mergeDrillDownTrees(responseData);
                const directTree =
                  responseData?.[0]?.drillDownTree ??
                  res?.responseList?.[0]?.drillDownTree ??
                  res?.drillDownTree;
                this.scoreTree = mergedTree?.length
                  ? mergedTree
                  : (Array.isArray(directTree) ? directTree : (directTree ? [directTree] : []));
                this.productId = res?.responseList?.[0]?.productId ?? responseData?.[0]?.productId;
              } else {
                const match = res?.responseList?.[0]?.find(
                  (item: any) => item?.periodType === this.data.periodType
                );
                this.productId = match?.productId;
                if (match?.drillDownTree) {
                  this.scoreTree = Array.isArray(match.drillDownTree)
                    ? match.drillDownTree
                    : [match.drillDownTree];
                } else {
                  this.scoreTree = [];
                  this.hasError = true;
                  this.errorMessage = 'No data found for the selected filters.';
                }
              }
            }
            this.loading = false;
          },
          error: (err) => {
            this.hasError = true;
            this.loading = false;
            this.errorMessage = 'No data found for the selected filters.';
          },
        });
    }
  }

  mergeDrillDownTrees(data: any[]) {
    // Helper function to merge nodes recursively
    function mergeNodes(startNode: any, endNode: any): any {
      const merged: any = {
        name: startNode?.name || endNode?.name,
        score: startNode?.score ?? null,
        simulatedScore: endNode?.score ?? null,
      };

      // If node has children, merge them too
      if (startNode?.children || endNode?.children) {
        const allNames = new Set([
          ...(startNode?.children?.map((c: any) => c.name) || []),
          ...(endNode?.children?.map((c: any) => c.name) || []),
        ]);

        merged.children = Array.from(allNames).map((name) =>
          mergeNodes(
            startNode?.children?.find((c: any) => c.name === name),
            endNode?.children?.find((c: any) => c.name === name)
          )
        );
      }

      return merged;
    }

    // Get START and END trees
    const startTree =
      data.find((d) => d.periodType === 'START')?.drillDownTree || [];
    const endTree =
      data.find((d) => d.periodType === 'END')?.drillDownTree || [];

    const allNames = new Set([
      ...startTree.map((n: any) => n.name),
      ...endTree.map((n: any) => n.name),
    ]);

    return Array.from(allNames).map((name) =>
      mergeNodes(
        startTree.find((n: any) => n.name === name),
        endTree.find((n: any) => n.name === name)
      )
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.bubbleClickSub) {
      this.bubbleClickSub.unsubscribe();
    }
  }

  reapplyExpandedState(newNodes: any[], oldNodes: any[]) {
    for (let i = 0; i < newNodes.length; i++) {
      const newNode = newNodes[i];
      const oldNode = oldNodes[i];
      if (oldNode?.expanded) {
        newNode.expanded = true;
      }
      if (newNode.children?.length && oldNode?.children?.length) {
        this.reapplyExpandedState(newNode.children, oldNode.children);
      }
    }
    this.cdr.detectChanges();
  }

  getToolTip(name: string): string {
    return this.def[name] || 'No definition available';
  }

  validateScore(eventOrValue: Event | string | number, node: any): void {
    let value: number;

    if (typeof eventOrValue === 'string' || typeof eventOrValue === 'number') {
      value = Number(eventOrValue);
    } else {
      const input = eventOrValue.target as HTMLInputElement;
      value = parseFloat(input.value);
    }

    const hasMax = this.scores?.includes(node.name);
    const max = hasMax ? 10 : Infinity;

    // Clamp value between min and max
    if (value < 0.1) value = 0.1;
    else if (value > max) value = max;

    // Update the input if it came from an event
    if (eventOrValue instanceof Event) {
      const input = eventOrValue.target as HTMLInputElement;
      input.value = value.toString(); // will keep decimal values
    }
    // Assign to correct simulatedScore location
    if (node.name === 'Actual Revenue ($M)') {
      this.scoreTree[1].children[0].children[0].simulatedScore = value;
      this.scoreTree[0].children[2].children[3].simulatedScore = value;
    } else {
      node.simulatedScore = value;
    }

    // Deep copy to trigger change detection (if needed)
    const updatedTree = JSON.parse(JSON.stringify(this.scoreTree));
    this.service.setSimulatedData(updatedTree);
  }

  getTooltipTableData(name: string) {
    switch (name) {
      case 'Quality Costs as % of Sales Score':
        return {
          headers: ['Interval', 'Score'],
          rows: [
            ['< 1%', 10],
            ['1% - 1.5%', 9],
            ['1.5% - 2%', 8],
            ['2% - 2.5%', 7],
            ['2.5% - 3%', 6],
            ['3% - 3.5%', 5],
            ['3.5% - 4%', 4],
            ['4% - 4.5%', 3],
            ['4.5% - 5%', 2],
            ['> 5%', 1],
          ],
        };
      // case 'Revenue Score':
      // return {
      //   headers: [
      //     'Vertical',
      //     'Quarterly Interval',
      //     'Annual Interval',
      //     'Score',
      //   ],
      //   rows: [
      //     ['MINING', '<4M', '<15M', 1],
      //     ['MINING', '4M-6M', '15M-22.5M', 2],
      //     ['MINING', '6M-8M', '22.5M-30M', 3],
      //     ['MINING', '8M-10M', '30M-37.5M', 4],
      //     ['MINING', '10M-12M', '37.5M-45M', 5],
      //     ['MINING', '12M-14M', '45M-52.5M', 6],
      //     ['MINING', '14M-16M', '52.5M-60M', 7],
      //     ['MINING', '16M-18M', '60M-67.5M', 8],
      //     ['MINING', '18M-20M', '67.5M-75M', 9],
      //     ['MINING', '>20M', '>75M', 10],
      //     ['NON-MINING', '<0.4M', '<1M', 1],
      //     ['NON-MINING', '0.4M-1.2M', '1M-3.4M', 2],
      //     ['NON-MINING', '1.2M-2M', '3.4M-5.8M', 3],
      //     ['NON-MINING', '2M-2.9M', '5.8M-8.1M', 4],
      //     ['NON-MINING', '2.9M-3.7M', '8.1M-10.5M', 5],
      //     ['NON-MINING', '3.7M-4.5M', '10.5M-12.9M', 6],
      //     ['NON-MINING', '4.5M-5.4M', '12.9M-15M', 7],
      //     ['NON-MINING', '5.4M-6.2M', '15M-17.6M', 8],
      //     ['NON-MINING', '6.2M-7M', '17.6M-20M', 9],
      //     ['NON-MINING', '>7M', '>20M', 10],
      //   ],
      // };
      case 'Aftermarket revenue as % of Forecasted aftermarket revenue':
        return {
          headers: ['%Range', 'Score'],
          rows: [
            ['<25', 1],
            ['25 - 35', 2],
            ['35 - 45', 3],
            ['45 - 55', 4],
            ['55 - 65', 5],
            ['65 - 75', 6],
            ['75 - 80', 7],
            ['80 - 90', 8],
            ['90 - 99', 9],
            ['>99', 10],
          ],
        };
      case 'Profitability / FF GM % (New Engines)':
        return {
          headers: ['Profitability %', 'Score'],
          rows: [
            ['< 20', 1],
            ['20 – 25', 2],
            ['25 – 30', 3],
            ['30 – 35', 4],
            ['35 – 38', 5],
            ['38 – 41', 6],
            ['41 – 44', 7],
            ['44 – 47', 8],
            ['47 – 51', 9],
            ['> 51', 10],
          ],
        };
      case 'Product Cost $/HP (Reactive Cost)':
        return {
          headers: ['Product Cost', 'Score'],
          rows: [
            ['<30', 10],
            ['30-45', 9],
            ['45-50', 8],
            ['50-60', 7],
            ['60-65', 6],
            ['65-70', 5],
            ['70-80', 4],
            ['80-90', 3],
            ['90-100', 2],
            ['100+', 1],
          ],
        };
      default:
        return null;
    }
  }

  onClose() {
    this.showCard = false;
    this.service.setBubbleClicked('');
  }

  onSave(): void {
    const cf = this.filters;

    const payload = {
      quarter_year: cf.quarter_year[0] || '',
      cleaned_vertical: cf.cleaned_vertical[0] || '',
      final_em: cf.final_em[0] || '',
      productPortfolio: this.productPortfolio || '',
      version: this.filters.version[0],
      drillDownTree: this.scoreTree,
    };

    const saveUrl: string = this.data?.saveUrl || '/spriced/platform';

    this.service.saveFutureExpectations(payload, saveUrl).subscribe({
      next: (res) => {
        this.snackBar.open('Saved successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
      },
      error: (err) => {
        this.snackBar.open('Something went wrong', 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
      },
    });
  }
  getScoreColor(score: number) {
    if (score == null) return { 'background-color': '#adacac90' };

    if (score >= 0 && score < 4)
      return { 'background-color': '#c4312b' };

    if (score >= 4 && score < 8)
      return { 'background-color': '#FFFF66' };

    if (score >= 8 && score <= 10)
      return { 'background-color': '#4CAF50' };

    return { 'background-color': '#adacac90' };
  }
}
